import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from './SEO';
import "bootstrap/dist/css/bootstrap.min.css";
import "../style/FleetDetail.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserFriends,
  faCouch,
  faSuitcaseRolling,
  faBriefcase,
  faShieldAlt,
  faCar,
  faGasPump,
  faSnowflake,
  faWifi,
  faMusic,
  faPhone,
  faMapMarkedAlt,
} from "@fortawesome/free-solid-svg-icons";

const FleetSedan = ({ currentLanguage }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Sedan images for carousel
  const sedanImages = [
    "/images/sedan.avif",
    "/images/sedan.avif", // You can add more Sedan images here
    "/images/sedan.avif",
  ];

  const vehicleData = {
    EN: {
      title: "Executive Sedan",
      subtitle: "Professional Elegance and Comfort",
      description: "Our executive sedan fleet offers the perfect combination of professional appearance, fuel efficiency, and comfort. Ideal for business professionals, airport transfers, and city travel.",
      specifications: {
        passengers: "Up to 4 Passengers",
        seats: "Premium Leather Seats",
        largeLuggage: "2-3 Large Bags",
        carryOn: "4-5 Carry-On Bags",
        fuelType: "Premium Gasoline",
        transmission: "Automatic",
        airConditioning: "Automatic Climate Control",
      },
      features: [
        "Executive leather interior",
        "Advanced driver assistance",
        "Built-in GPS navigation",
        "Wireless phone charging",
        "Premium audio system",
        "Privacy tinted windows",
        "Professional chauffeur service",
        "Complimentary amenities",
      ],
      perfectFor: [
        "Executive business travel",
        "Airport and hotel transfers",
        "Corporate meetings",
        "Professional appointments",
        "City-to-city travel",
        "VIP transportation",
      ],
    },
    ES: {
      title: "Sedan Ejecutivo",
      subtitle: "Elegancia Profesional y Comodidad",
      description: "Nuestra flota de sedanes ejecutivos ofrece la combinación perfecta de apariencia profesional, eficiencia de combustible y comodidad. Ideal para profesionales de negocios, traslados al aeropuerto y viajes por la ciudad.",
      specifications: {
        passengers: "Hasta 4 Pasajeros",
        seats: "Asientos de Cuero Premium",
        largeLuggage: "2-3 Maletas Grandes",
        carryOn: "4-5 Equipajes de Mano",
        fuelType: "Gasolina Premium",
        transmission: "Automático",
        airConditioning: "Control de Clima Automático",
      },
      features: [
        "Interior de cuero ejecutivo",
        "Asistencia avanzada al conductor",
        "Navegación GPS integrada",
        "Carga inalámbrica de teléfono",
        "Sistema de audio premium",
        "Ventanas tintadas de privacidad",
        "Servicio de chofer profesional",
        "Comodidades de cortesía",
      ],
      perfectFor: [
        "Viajes ejecutivos de negocios",
        "Traslados al aeropuerto y hotel",
        "Reuniones corporativas",
        "Citas profesionales",
        "Viajes ciudad a ciudad",
        "Transporte VIP",
      ],
    },
    AR: {
      title: "سيدان تنفيذية",
      subtitle: "الأناقة المهنية والراحة",
      description: "يوفر أسطول السيدان التنفيذي لدينا المزيج المثالي من المظهر المهني وكفاءة الوقود والراحة. مثالي لرجال الأعمال المحترفين ونقل المطار والسفر في المدينة.",
      specifications: {
        passengers: "حتى 4 ركاب",
        seats: "مقاعد جلدية فاخرة",
        largeLuggage: "2-3 حقائب كبيرة",
        carryOn: "4-5 حقائب يد",
        fuelType: "بنزين ممتاز",
        transmission: "أوتوماتيكي",
        airConditioning: "تحكم مناخي أوتوماتيكي",
      },
      features: [
        "تصميم داخلي جلدي تنفيذي",
        "مساعدة متقدمة للسائق",
        "ملاحة GPS مدمجة",
        "شحن لاسلكي للهاتف",
        "نظام صوتي فاخر",
        "نوافذ ملونة للخصوصية",
        "خدمة سائق محترف",
        "وسائل راحة مجانية",
      ],
      perfectFor: [
        "السفر التنفيذي للأعمال",
        "نقل المطار والفندق",
        "الاجتماعات المؤسسية",
        "المواعيد المهنية",
        "السفر من مدينة لأخرى",
        "النقل المميز",
      ],
    },
  };

  const data = vehicleData[currentLanguage] || vehicleData.EN;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % sedanImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + sedanImages.length) % sedanImages.length);
  };

  return (
    <>
      <SEO 
        title="Executive Sedan Service | Luxury Car Transportation in Jacksonville"
        description="Premium executive sedan service in Jacksonville. Professional chauffeurs, luxury vehicles perfect for business travel, airport transfers, and special occasions. Book now!"
        keywords="executive sedan Jacksonville, luxury car service, business transportation, airport sedan, professional chauffeur, executive travel"
        canonical="/fleet/sedan"
      />
      <div className="fleet-detail-container">
        {/* Hero Section with Carousel */}
        <section className="fleet-detail-hero">
          <div className="fleet-carousel-container">
            <div className="fleet-carousel">
              <img
                src={sedanImages[currentImageIndex]}
                alt="Executive Sedan"
                className="fleet-detail-image"
              />
              <div className="fleet-carousel-overlay"></div>
              
              {/* Carousel Controls */}
              <button className="fleet-carousel-btn prev" onClick={prevImage}>
              <span>‹</span>
            </button>
            <button className="fleet-carousel-btn next" onClick={nextImage}>
              <span>›</span>
            </button>
            
            {/* Carousel Indicators */}
            <div className="fleet-carousel-indicators">
              {sedanImages.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </div>
          
          {/* Hero Content */}
          <div className="fleet-hero-content">
            <h1 className="fleet-detail-title">{data.title}</h1>
            <p className="fleet-detail-subtitle">{data.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Vehicle Details Section */}
      <section className="fleet-detail-content">
        <div className="container">
          <div className="row">
            {/* Description */}
            <div className="col-lg-8">
              <div className="fleet-description">
                <h2>About This Vehicle</h2>
                <p>{data.description}</p>
              </div>

              {/* Specifications */}
              <div className="fleet-specifications">
                <h3>Vehicle Specifications</h3>
                <div className="specs-grid">
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faUserFriends} className="spec-icon" />
                    <span>{data.specifications.passengers}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faCouch} className="spec-icon" />
                    <span>{data.specifications.seats}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faSuitcaseRolling} className="spec-icon" />
                    <span>{data.specifications.largeLuggage}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faBriefcase} className="spec-icon" />
                    <span>{data.specifications.carryOn}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faGasPump} className="spec-icon" />
                    <span>{data.specifications.fuelType}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faCar} className="spec-icon" />
                    <span>{data.specifications.transmission}</span>
                  </div>
                  <div className="spec-item">
                    <FontAwesomeIcon icon={faSnowflake} className="spec-icon" />
                    <span>{data.specifications.airConditioning}</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="fleet-features">
                <h3>Premium Features</h3>
                <div className="features-grid">
                  {data.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Perfect For Sidebar */}
            <div className="col-lg-4">
              <div className="fleet-sidebar">
                <div className="perfect-for-card">
                  <h3>Perfect For</h3>
                  <ul className="perfect-for-list">
                    {data.perfectFor.map((occasion, index) => (
                      <li key={index}>
                        <FontAwesomeIcon icon={faMapMarkedAlt} className="list-icon" />
                        {occasion}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Card */}
                <div className="fleet-cta-card">
                  <h4>Ready to Book?</h4>
                  <p>Professional transportation for business excellence.</p>
                  <Link to="/ReservationForm" className="fleet-cta-btn">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    Book Now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default FleetSedan;
